import { formatPromoDeadline, usePricing } from '../contexts/PricingContext'
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan'
import { day15Share, day45Share, getPricing } from '../utils/roiCalculations'
import { useFinancialModel } from './useFinancialModel'

export const usd = (value: number) =>
  '$' + Math.round(value).toLocaleString('en-US')

/** The four figures every page-2 pricing layout shows, from live settings. */
export function usePricingFigures(includeLeader: boolean) {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    monthlyTotal,
    buildFee,
    splitBuildFee,
    hasImplementationFee,
    leaderBuildFee,
    hasRecruiter,
    isPromo,
    promoDeadline,
    regularImplementationFee,
  } = usePricing()
  const { inputs, leaderMonths } = useFinancialModel()
  // Split payments land in the rep campaign's 2nd and 3rd months, counted
  // from signing (after any leader search months).
  const splitMonthA = (includeLeader ? leaderMonths : 0) + 2
  const pricing = getPricing(inputs)

  const implementation = buildFee + (includeLeader ? leaderBuildFee : 0)
  const plan = getBuildPaymentPlan(implementation)
  // Rounded down so the range never beats the cost-per-hire floor.
  const hiresLow = Math.max(1, Math.floor(adSpend / cpaHigh))
  const hiresHigh = Math.max(hiresLow + 1, Math.floor(adSpend / cpaLow))

  // Estimated bonuses a month's hires earn, with the same retention formula
  // as the model and the tier table: launched reps × the share still active
  // at each milestone, rounded up to whole reps.
  const pctOf = (value: number) => Math.min(1, Math.max(0, (value || 0) / 100))
  const launchRate = pctOf(inputs.pctLaunched)
  const pct30 = pctOf(inputs.pct30)
  const pct90 = pctOf(inputs.pct90)
  const pct60 = Math.sqrt(pct90)
  const bonusesFor = (hires: number) => {
    const launched = hires * launchRate
    return (
      Math.ceil(launched * day15Share(pct30)) * pricing.bonusDay15 +
      Math.ceil(launched * day45Share(pct30, pct60)) * pricing.bonusDay45 +
      Math.round(launched * pct30 * pct90) * pricing.bonusDay90
    )
  }
  const allIn = (hires: number) =>
    (monthlyTotal + adSpend + bonusesFor(hires)) / hires

  return {
    showImplementation: hasImplementationFee && implementation > 0,
    implementation,
    implementationNote: isPromo
      ? splitBuildFee
        ? `promo · ${usd(plan.down)} × 3 · regularly ${usd(regularImplementationFee)}`
        : regularImplementationFee > implementation
        ? `promo price, regularly ${usd(regularImplementationFee)}`
        : 'promo price'
      : splitBuildFee
        ? `${usd(plan.down)} at signing, then ${usd(plan.monthly)} in Months ${splitMonthA} and ${splitMonthA + 1}`
        : 'one time, at signing',
    // Promo deadline, set apart in every layout so it can't be missed.
    promoDeadlineText: isPromo
      ? `Sign by ${formatPromoDeadline(promoDeadline)}`
      : null,
    monthly: monthlyTotal,
    monthlyNote: hasRecruiter ? 'incl. corporate recruiter' : 'all-in service',
    bonus: pricing.bonusDay15,
    bonusNote: 'per rep active at day 15 in the field',
    // Flagship's day-45 retention bonus (0 when waived or on Monthly).
    retentionBonus: pricing.bonusDay45,
    retentionNote: 'per rep still active at day 45 in the field',
    adSpend,
    adsNote: 'paid directly to job platforms',
    hiresLow,
    hiresHigh,
    // All-in per hire: service + ads + the estimated bonuses those hires earn.
    costLow: allIn(hiresHigh),
    costHigh: allIn(hiresLow),
  }
}
